import { useDisconnect, useWalletClient, usePublicClient, useSwitchChain } from 'wagmi';
import { useAppKit, useAppKitAccount, useAppKitNetwork } from '@reown/appkit/react';
import { useEffect, useState } from 'react';
import { environment } from '@/config/environment';

/**
 * Unlock track, mirroring the Category enum in TokenLock.sol. The track selects
 * which fixed unlock cliff date applies -- the dates live on the factory and are
 * read per lock, so nothing here hardcodes them. Onboard-anchored vesting is the
 * same for both. The contract encodes this as a uint8, so viem decodes it as a
 * number.
 */
export const Category = { INVESTOR: 0, TEAM: 1 } as const;
export type Category = (typeof Category)[keyof typeof Category];

/** Human label for an unlock track, for pills and schedule copy. */
export const categoryLabel = (category: number): string =>
  category === Category.TEAM ? 'Team' : 'Investor';

export interface LockInfo {
  totalAmount: bigint;
  unlockedAmount: bigint;
  beneficiary: string;
  startTime: bigint;
  vestedAmount: bigint;
  /**
   * What claim() would actually pay right now: earned, already capped by the
   * tokens the lock holds. Never show a larger number as claimable.
   */
  availableAmount: bigint;
  isInitialized: boolean;
  offboardTime: bigint;
  unlockScheduleAmount: bigint;
  /** Fixed date this lock's track releases its 25% cliff; 0 until the owner sets it. */
  unlockCliffTime: bigint;
  /** Unlock track; see {@link Category}. */
  category: number;
  /**
   * Earned by the schedule and not yet claimed, regardless of funding. Equal to
   * availableAmount once the lock is funded that far, larger while it is not.
   */
  earnedUnclaimed: bigint;
  /** Tokens actually sitting in the lock — the hard ceiling on claim(). */
  lockBalance: bigint;
  /** Next timestamp the entitlement grows; 0 when nothing further will release. */
  nextUnlockTime: bigint;
  /**
   * The ceiling on what this lock can EVER pay out, which is not the same as
   * totalAmount: an off-boarded lock froze at what had vested by its freeze
   * date, and a cancelled one is 0. Every "still locked" / "remaining" figure
   * must be computed against this, never against totalAmount.
   *
   * On a v7 or v9 factory this falls back to totalAmount, because cancellation
   * does not exist there.
   */
  maxEntitlement: bigint;
  /**
   * Terminal. A cancelled grant forfeits EVERYTHING the beneficiary has not
   * already claimed, including tokens they had vested and earned but never
   * collected. maxEntitlement, vestedAmount, availableAmount, earnedUnclaimed,
   * unlockScheduleAmount and nextUnlockTime are all 0 once this is true; only
   * unlockedAmount, what they already took, survives. Always false on v7/v9.
   */
  cancelled: boolean;
  /**
   * Tokens sitting in the lock above anything it can ever pay. Non-zero means a
   * ceiling dropped after a top-up and the treasury is short until the owner
   * calls returnLockExcess. Always 0 on v7/v9.
   */
  excessFunding: bigint;
}

export interface VestingSchedule {
  date: Date;
  percentage: number;
  amount: bigint;
  unlocked: boolean;
  /** Vesting covers this tranche; an unlocked-but-unvested tranche is not yet claimable. */
  vested: boolean;
  /**
   * The holder has already been paid everything up to and including this
   * tranche: its cumulative sits at or below unlockedAmount.
   *
   * This exists because a downward resize rescales the PAST. It can leave a
   * tranche whose date has not arrived already covered by what has been claimed,
   * and such a tranche is neither upcoming nor forfeited — the tokens are in the
   * holder's wallet. It takes precedence over `forfeited` for exactly that
   * reason: a cancelled grant forfeits what was never collected, never what was.
   */
  settled: boolean;
  /**
   * This tranche's cumulative sits above the grant's ceiling, so it will never
   * pay. Cancellation and an effective off-board date are two ways to get here;
   * a downward resize is the third, and it sets neither flag.
   */
  forfeited: boolean;
}

export function useWeb3() {
  const { address, isConnected } = useAppKitAccount();
  const { chainId } = useAppKitNetwork();
  const { open } = useAppKit();
  const { disconnect } = useDisconnect();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const { switchChain } = useSwitchChain();

  const [isCorrectNetwork, setIsCorrectNetwork] = useState(true);
  const [showNetworkDialog, setShowNetworkDialog] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  // Check if we're on the correct network
  useEffect(() => {
    if (isConnected && chainId) {
      const correct = chainId === environment.chainId;
      setIsCorrectNetwork(correct);
      if (!correct) {
        setShowNetworkDialog(true);
      }
    }
  }, [isConnected, chainId]);

  const connectWallet = async () => {
    try {
      setIsConnecting(true);
      await open();
    } catch (error) {
      console.error('Failed to open wallet modal:', error);
      throw error;
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectWallet = async () => {
    try {
      await disconnect();
    } catch (error) {
      console.error('Failed to disconnect:', error);
    }
  };

  const openModal = () => {
    open();
  };

  const switchToCorrectNetwork = async () => {
    try {
      await switchChain({ chainId: environment.chainId });
      setShowNetworkDialog(false);
    } catch (error) {
      console.error('Failed to switch network:', error);
      throw error;
    }
  };

  const closeNetworkDialog = () => {
    setShowNetworkDialog(false);
  };

  const formatAddress = (address: string): string => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatTokenAmount = (amount: bigint): string => {
    const decimals = environment.tokenDecimals;
    const divisor = BigInt(10 ** decimals);
    const wholePart = amount / divisor;
    const fractionalPart = amount % divisor;
    const fractionalStr = fractionalPart.toString().padStart(decimals, '0');

    // Always show 3 decimal places
    const displayDecimals = 3;
    const truncatedFractional = fractionalStr.slice(0, displayDecimals).padEnd(displayDecimals, '0');

    // Thousands separators — large allocations are unreadable without them.
    const groupedWhole = wholePart.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return `${groupedWhole}.${truncatedFractional}`;
  };

  return {
    // State
    address,
    isConnected,
    chainId,
    isCorrectNetwork,
    showNetworkDialog,
    isConnecting,
    walletClient,
    publicClient,

    // Actions
    connectWallet,
    disconnectWallet,
    openModal,
    switchToCorrectNetwork,
    closeNetworkDialog,

    // Utilities
    formatAddress,
    formatTokenAmount,
  };
}
